import { gql, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { BLOCKED_EMAIL_FRAGMENT } from './blocked-email.fragments';
import { BlockedEmailEntity } from './blocked-email.types';
import { GetBlockedEmailQuery } from './use-blocked-email';

export interface CreateBlockedEmailMutation {
  createBlockedEmail: Omit<BlockedEmailEntity, 'createdByPlatformUser'>[];
}

export const CREATE_BLOCKED_EMAIL_QUERY = gql`
  mutation CreateBlockedEmail(
    $data: BlockedEmailDataInput #    $includeCreatedByUser: Boolean = false
  ) {
    createBlockedEmail(data: $data) {
      ...BlockedEmailFields
    }
  }
  ${BLOCKED_EMAIL_FRAGMENT}
`;

export type CreateBlockedEmailMutationParams = {
  data: Pick<BlockedEmailEntity, 'blockedEmail' | 'partnerId' | 'createdByPlatformUserId'>;
};

export const useCreateBlockedEmail = () => {
  const partnerId = useGlobalStore.use.selectedPartnerId() || PLATFORM_PARTNER_ID;
  const { user } = useCurrentUserProfile();
  const { showErrorAlert } = useNotifications();

  const [createBlockedEmailMutation, { loading: isCreating }] = useMutation<
    CreateBlockedEmailMutation,
    CreateBlockedEmailMutationParams
  >(CREATE_BLOCKED_EMAIL_QUERY, {
    update(cache, { data }) {
      if (!data?.createBlockedEmail?.length) {
        return;
      }

      cache.modify({
        fields: {
          getBlockedEmail(cachedRef = {}) {
            const cachedData = cachedRef as GetBlockedEmailQuery['getBlockedEmail'];
            const newBlockedEmailRef = cache.writeFragment({
              data: data.createBlockedEmail[0],
              fragment: BLOCKED_EMAIL_FRAGMENT,
              broadcast: false,
            });

            return {
              ...cachedData,
              records: [
                ...(cachedData.records?.length ? cachedData.records : []),
                newBlockedEmailRef,
              ],
              metadata: {
                ...cachedData.metadata,
                totalRecords: (cachedData.metadata?.totalRecords ?? 0) + 1,
              },
            };
          },
        },
      });
    },
    optimisticResponse: (params) => ({
      createBlockedEmail: [
        {
          __typename: 'BlockedEmail',
          id: new Date().getTime().toString(),
          blockedEmailId: new Date().getTime().toString(),
          partnerId: params.data.partnerId,
          blockedEmail: params.data.blockedEmail,
          createdAt: new Date().toLocaleTimeString(),
          updatedAt: new Date().toLocaleTimeString(),
          createdByPlatformUserId: user?.userId || '',
        },
      ],
    }),
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to create blocked email, please try again');
    },
  });

  const createBlockedEmail = useCallback(
    async (blockedEmail: string) => {
      if (!user?.userId) {
        console.error('Cannot create blocked email. Current user is not found');
        return;
      }
      await createBlockedEmailMutation({
        variables: {
          data: {
            blockedEmail: blockedEmail.toLowerCase(),
            partnerId,
            createdByPlatformUserId: user.userId,
          },
        },
      });
    },
    [createBlockedEmailMutation, partnerId, user?.userId],
  );

  return {
    createBlockedEmail,
    isCreating,
  };
};
